import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useGetV2AdminOrganizationsQuery } from '../store/hooks';
import {
  useConvertAdminNoteTemplateToOrgMutation,
  type EffectiveNoteTemplateSummary,
} from '../store/hooks';

interface ConvertToOrgDialogProps {
  open: boolean;
  onClose: () => void;
  template: EffectiveNoteTemplateSummary | null;
}

export function ConvertToOrgDialog({ open, onClose, template }: ConvertToOrgDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [targetOrgId, setTargetOrgId] = useState<string | null>(null);
  const [orgSearchQuery, setOrgSearchQuery] = useState('');

  const [convertTemplate, { isLoading: isConverting }] = useConvertAdminNoteTemplateToOrgMutation();

  // Search organizations
  const { data: orgsData, isLoading: isSearchingOrgs } = useGetV2AdminOrganizationsQuery(
    {},
    { skip: !orgSearchQuery || orgSearchQuery.length < 2 },
  );

  const handleConvert = useCallback(async () => {
    if (!template || !targetOrgId) {
      enqueueSnackbar('Please select a target organization', { variant: 'error' });
      return;
    }

    const scope = template.template?.owner_scope || 'user';
    if (scope !== 'user') {
      enqueueSnackbar('Only user-scoped templates can be converted to org templates', {
        variant: 'error',
      });
      return;
    }

    try {
      await convertTemplate({
        adminConvertToOrgRequest: {
          template_id: template.template?.id || '',
          organization_id: targetOrgId,
        },
      }).unwrap();

      enqueueSnackbar('Template converted to organization template successfully', {
        variant: 'success',
      });
      handleClose();
    } catch (error: any) {
      enqueueSnackbar(
        error?.data?.message || 'Failed to convert template',
        { variant: 'error' },
      );
    }
  }, [template, targetOrgId, convertTemplate, enqueueSnackbar]);

  const handleClose = useCallback(() => {
    setTargetOrgId(null);
    setOrgSearchQuery('');
    onClose();
  }, [onClose]);

  const organizations = orgsData?.items || [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Convert Template to Organization Template</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {template && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Template
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {template.revision?.title || 'Unnamed Template'}
              </Typography>
              {template.revision?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {template.revision.description}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Current scope: <strong>{template.template?.owner_scope || 'user'}</strong>
              </Typography>
            </Box>
          )}

          <Autocomplete
            options={organizations}
            getOptionLabel={(option: any) => option.name || option.organization_id}
            loading={isSearchingOrgs}
            onInputChange={(_, value) => setOrgSearchQuery(value)}
            onChange={(_, value: any) => setTargetOrgId(value?.organization_id || null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target Organization *"
                placeholder="Search for an organization..."
                helperText="Select the organization to convert this template to"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearchingOrgs ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Alert severity="warning">
            <Typography variant="body2" fontWeight={600} gutterBottom>
              This is a permanent change
            </Typography>
            <Typography variant="body2">
              Converting this template will change its scope from "user" to "org" in place. The
              original creator ({template?.template?.user_id || 'Unknown'}) will be preserved, but the template will now
              be accessible to all members of the selected organization.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isConverting}>
          Cancel
        </Button>
        <Button
          onClick={handleConvert}
          variant="contained"
          color="warning"
          disabled={!targetOrgId || isConverting || template?.template?.owner_scope !== 'user'}
        >
          {isConverting ? <CircularProgress size={20} /> : 'Convert to Org'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

